// History: every-500m stroke-curve snapshots with "Pin as ideal".
import { useEffect, useState } from 'react';
import { Pin, PinOff } from 'lucide-react';
import { toast } from 'sonner';
import { loadIdealCurves, subscribeIdeal, getIdeal, pinIdealCurve, clearIdealCurve } from '@/lib/idealCurves';
import { trimDrivePairs } from '@/lib/driveWindow';

const W = 160;
const H = 70;
const COLORS = [['#3B82F6', 0.25], ['#3FD6A4', 0.55], ['#21B86A', 1]];

function CurveSvg({ strokes, ghost }) {
  const all = ghost ? [...strokes, ghost] : strokes;
  const flat = all.flat();
  const maxAbs = Math.max(0.5, ...flat.map((p) => Math.abs(p[1]))) * 1.1;
  const positiveOnly = Math.min(0, ...flat.map((p) => p[1])) >= 0;
  const base = positiveOnly ? H - 4 : H / 2;
  const span = positiveOnly ? H - 8 : H / 2 - 4;
  // X = % of stroke: each curve is normalised by its own duration (last dt)
  const d = (s) => s.map(([t, v], i) => `${i ? 'L' : 'M'}${((t / (s[s.length - 1][0] || 1)) * W).toFixed(1)},${(base - (v / maxAbs) * span).toFixed(1)}`).join(' ');
  const offset = COLORS.length - strokes.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="none">
      <line x1="0" x2={W} y1={base} y2={base} stroke="#94a3b8" strokeDasharray="3 3" strokeWidth="0.8" />
      {ghost && <path d={d(ghost)} fill="none" stroke="#F5C518" strokeWidth="1.8" strokeDasharray="5 4" />}
      {strokes.map((s, i) => {
        const [c, o] = COLORS[i + offset];
        return <path key={i} d={d(s)} fill="none" stroke={c} strokeOpacity={o} strokeWidth={i === strokes.length - 1 ? 2.2 : 1.4} strokeLinejoin="round" />;
      })}
    </svg>
  );
}

function SnapshotCard({ snap, workoutId, ghost, isPinned }) {
  const [busy, setBusy] = useState(false);
  const lastStroke = snap.strokes[snap.strokes.length - 1] || [];
  const peak = lastStroke.length ? Math.max(...lastStroke.map((p) => p[1])).toFixed(snap.kind === 'erg' ? 0 : 1) : '—';
  const pin = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const last = snap.strokes[snap.strokes.length - 1];
      await pinIdealCurve(snap.kind, last, { source_workout_id: workoutId, source_distance_m: snap.distance_m });
      toast.success(`Pinned ${snap.distance_m}m stroke as your ideal ${snap.kind} curve`);
    } catch (_e) {
      toast.error('Could not pin stroke');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className={`shrink-0 w-44 rounded-xl border p-2 bg-white dark:bg-zinc-900 ${isPinned ? 'border-amber-400 ring-1 ring-amber-300' : 'border-zinc-200 dark:border-zinc-700'}`}
      data-testid={`stroke-shape-card-${snap.distance_m}`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-bold text-zinc-700 dark:text-zinc-200" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {snap.distance_m}m
          <span className="ml-1.5 text-[10px] font-semibold text-emerald-600" data-testid={`stroke-shape-peak-${snap.distance_m}`}>
            {peak}{snap.kind === 'erg' ? 'N' : ' m/s²'}
          </span>
        </span>
        <button
          onClick={pin}
          disabled={busy}
          className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 hover:bg-amber-100 hover:text-amber-700 transition-colors disabled:opacity-50"
          data-testid={`stroke-shape-pin-${snap.distance_m}`}
        >
          <Pin className="w-3 h-3" /> {isPinned ? 'Pinned' : 'Pin'}
        </button>
      </div>
      <CurveSvg strokes={snap.kind === 'boat' ? snap.strokes.map(trimDrivePairs) : snap.strokes} ghost={ghost} />
    </div>
  );
}

export default function StrokeShapeStrip({ workout }) {
  const snaps = workout?.curve_snapshots || [];
  const [ideals, setIdeals] = useState({});
  useEffect(() => {
    loadIdealCurves(true).then((c) => setIdeals({ ...c }));
    return subscribeIdeal((c) => setIdeals({ ...(c || {}) }));
  }, []);
  if (!snaps.length) return null;
  const kinds = [...new Set(snaps.map((s) => s.kind))];
  const idealFor = (k) => {
    const ideal = ideals[k] || getIdeal(k);
    return ideal && k === 'boat' ? { ...ideal, points: trimDrivePairs(ideal.points) } : ideal;
  };
  const pinnedKinds = kinds.filter((k) => idealFor(k));
  const ghost = pinnedKinds.length > 0;

  const clear = async () => {
    try { await Promise.all(pinnedKinds.map(clearIdealCurve)); toast.success('Ideal curve cleared'); } catch (_e) { toast.error('Could not clear ideal'); }
  };

  return (
    <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-4" data-testid="stroke-shape-strip">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700 dark:text-zinc-200">
            Stroke Shape · {kinds.map((k) => (k === 'erg' ? 'Force Curve' : 'Acceleration')).join(' / ')}
          </h3>
          <p className="text-xs text-zinc-500">Last 3 strokes every 500m{ghost ? ' · dashed gold = your pinned ideal' : ' · pin a stroke to set your ideal'}</p>
        </div>
        {ghost && (
          <button onClick={clear} className="flex items-center gap-1 text-xs font-bold text-zinc-500 hover:text-red-600 transition-colors" data-testid="stroke-shape-clear-ideal">
            <PinOff className="w-3.5 h-3.5" /> Clear ideal
          </button>
        )}
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1" data-testid="stroke-shape-cards">
        {snaps.map((s) => (
          <SnapshotCard
            key={`${s.kind}-${s.distance_m}`}
            snap={s}
            workoutId={workout.workout_id}
            ghost={idealFor(s.kind)?.points || null}
            isPinned={idealFor(s.kind)?.source_workout_id === workout.workout_id && idealFor(s.kind)?.source_distance_m === s.distance_m}
          />
        ))}
      </div>
    </div>
  );
}
